import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, Info, RefreshCw, Copy } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { calculateTrgFromQu } from '@/lib/calculations';

export const SoilProfileTab = ({ pileInputs, setPileInputs, soilLayers, setSoilLayers, onCopyToAll, pileCount }) => {
  const [autoComputeTrg, setAutoComputeTrg] = useState(false);
  const [autoFillFrom, setAutoFillFrom] = useState(true);

  // Update hole oversize
  const updateHoleOversize = (value) => {
    setPileInputs(prev => ({
      ...prev,
      holeOversize: parseFloat(value) || 0
    }));
  };

  // Update a soil layer
  const updateLayer = (id, key, value) => {
    setSoilLayers(prev => {
      const layerIndex = prev.findIndex(l => l.id === id);
      if (layerIndex === -1) return prev;
      
      return prev.map((layer, index) => {
        // Update the current layer
        if (layer.id === id) {
          const updatedLayer = { ...layer, [key]: parseFloat(value) || 0 };
          
          // Auto-compute T_RG if enabled and qu is changed
          if (autoComputeTrg && key === 'qu') {
            updatedLayer.trg = Math.round(calculateTrgFromQu(updatedLayer.qu) * 100) / 100;
          }
          
          return updatedLayer;
        }
        
        // Auto-fill next layer's "from" if current layer's "to" was changed
        if (autoFillFrom && key === 'to' && index === layerIndex + 1) {
          return { ...layer, from: parseFloat(value) || 0 };
        }
        
        return layer;
      });
    });
  };

  // Add a new layer
  const addLayer = () => {
    const lastLayer = soilLayers[soilLayers.length - 1];
    const newId = Math.max(...soilLayers.map(l => l.id), 0) + 1;
    const newFrom = lastLayer ? lastLayer.to : 0;
    const newTo = newFrom + 10;
    const newQu = lastLayer ? lastLayer.qu : 2.0;
    
    setSoilLayers(prev => [...prev, {
      id: newId,
      from: newFrom,
      to: newTo,
      qu: newQu,
      gamma: 23,
      rqd: 50,
      trg: autoComputeTrg ? calculateTrgFromQu(newQu) : 1.0
    }]);
  };

  // Remove a layer
  const removeLayer = (id) => {
    if (soilLayers.length <= 1) return;
    setSoilLayers(prev => prev.filter(l => l.id !== id));
  };

  // Recompute all T_RG values from q_u
  const recomputeAllTrg = () => {
    setSoilLayers(prev => prev.map(layer => ({
      ...layer,
      trg: Math.round(calculateTrgFromQu(layer.qu) * 100) / 100
    })));
  };

  // Toggle auto-compute
  const handleAutoComputeToggle = (checked) => {
    setAutoComputeTrg(checked);
    if (checked) {
      recomputeAllTrg();
    }
  };

  // Calculate max depth
  const maxDepth = soilLayers.length > 0 ? Math.max(...soilLayers.map(l => l.to)) : 0;

  return (
    <div className="space-y-6" data-testid="soil-profile-tab">
      {/* Copy to All Piles Button */}
      {pileCount > 1 && onCopyToAll && (
        <Card className="border-dashed border-amber-500/50 bg-amber-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium">Apply to all piles</span>
                <span className="text-muted-foreground ml-2">Copy current soil profile to all {pileCount - 1} other piles</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onCopyToAll}
                className="border-amber-500 hover:bg-amber-500/10"
                data-testid="copy-soil-profile-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy to All Piles
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Borehole Parameters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Borehole Parameters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="form-group">
              <Label htmlFor="holeOversize" className="form-label">
                Hole Oversize (mm)
              </Label>
              <Input
                id="holeOversize"
                type="number"
                value={pileInputs.holeOversize}
                onChange={(e) => updateHoleOversize(e.target.value)}
                className="engineering-input"
                data-testid="input-hole-oversize"
              />
              <p className="form-hint">Drilled hole diameter - Pile OD</p>
            </div>

            <div className="form-group">
              <Label className="form-label text-muted-foreground">Max Depth</Label>
              <div className="h-10 flex items-center font-mono text-lg" data-testid="max-depth-display">
                {maxDepth.toFixed(1)} m
              </div>
            </div>

            <div className="form-group">
              <Label className="form-label text-muted-foreground">Number of Layers</Label>
              <div className="h-10 flex items-center font-mono text-lg" data-testid="layer-count-display">
                {soilLayers.length}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Helper Options */}
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Auto-fill From depth */}
            <div className="flex items-center gap-3">
              <Switch
                id="autoFillFrom"
                checked={autoFillFrom}
                onCheckedChange={setAutoFillFrom}
                data-testid="auto-fill-from-switch"
              />
              <div>
                <Label htmlFor="autoFillFrom" className="text-sm font-medium cursor-pointer">
                  Auto-fill "From" depth
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Automatically update next layer's "From" when you change a layer's "To" value
                </p>
              </div>
            </div>

            {/* Auto-compute T_RG */}
            <div className="flex items-center gap-3">
              <Switch
                id="autoComputeTrg"
                checked={autoComputeTrg}
                onCheckedChange={handleAutoComputeToggle}
                data-testid="auto-compute-trg-switch"
              />
              <div>
                <Label htmlFor="autoComputeTrg" className="text-sm font-medium cursor-pointer">
                  Auto-compute T<sub>RG</sub> from q<sub>u</sub>
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Abbs & Needham: T<sub>RG</sub> = 0.3×q<sub>u</sub> (capped at 1.2 MPa)
                </p>
              </div>
            </div>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={recomputeAllTrg}
                    data-testid="recompute-trg-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Recompute All
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Recalculate T<sub>RG</sub> for all layers using current q<sub>u</sub> values</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      {/* Soil/Rock Layers Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            Soil / Rock Layers
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    Define soil/rock layers with depth intervals and properties. 
                    T<sub>RG</sub> (rock-grout bond) is the key parameter for shaft capacity calculation.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={addLayer}
            data-testid="add-layer-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Layer
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Layer</TableHead>
                  <TableHead className="text-right">From (m)</TableHead>
                  <TableHead className="text-right">To (m)</TableHead>
                  <TableHead className="text-right">
                    q<sub>u</sub> (MPa)
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="ml-1">
                          <Info className="h-3 w-3 inline text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Unconfined compressive strength</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="text-right">γ (kN/m³)</TableHead>
                  <TableHead className="text-right">RQD (%)</TableHead>
                  <TableHead className="text-right">
                    T<sub>RG</sub> (MPa)
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger className="ml-1">
                          <Info className="h-3 w-3 inline text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Rock-grout bond strength (editable)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {soilLayers.map((layer, index) => (
                  <TableRow key={layer.id} data-testid={`soil-layer-row-${index}`}>
                    <TableCell className="font-medium">{index + 1}</TableCell>
                    <TableCell className="text-right p-1">
                      <Input
                        type="number"
                        step="0.1"
                        value={layer.from}
                        onChange={(e) => updateLayer(layer.id, 'from', e.target.value)}
                        className="w-20 ml-auto engineering-input"
                        data-testid={`input-layer-from-${index}`}
                      />
                    </TableCell>
                    <TableCell className="text-right p-1">
                      <Input
                        type="number"
                        step="0.1"
                        value={layer.to}
                        onChange={(e) => updateLayer(layer.id, 'to', e.target.value)}
                        className="w-20 ml-auto engineering-input"
                        data-testid={`input-layer-to-${index}`}
                      />
                    </TableCell>
                    <TableCell className="text-right p-1">
                      <Input
                        type="number"
                        step="0.1"
                        value={layer.qu}
                        onChange={(e) => updateLayer(layer.id, 'qu', e.target.value)}
                        className="w-20 ml-auto engineering-input"
                        data-testid={`input-layer-qu-${index}`}
                      />
                    </TableCell>
                    <TableCell className="text-right p-1">
                      <Input
                        type="number"
                        value={layer.gamma}
                        onChange={(e) => updateLayer(layer.id, 'gamma', e.target.value)}
                        className="w-16 ml-auto engineering-input"
                        data-testid={`input-layer-gamma-${index}`}
                      />
                    </TableCell>
                    <TableCell className="text-right p-1">
                      <Input
                        type="number"
                        value={layer.rqd}
                        onChange={(e) => updateLayer(layer.id, 'rqd', e.target.value)}
                        className="w-16 ml-auto engineering-input"
                        data-testid={`input-layer-rqd-${index}`}
                      />
                    </TableCell>
                    <TableCell className="text-right p-1">
                      <Input
                        type="number"
                        step="0.01"
                        value={layer.trg}
                        onChange={(e) => updateLayer(layer.id, 'trg', e.target.value)}
                        className={`w-20 ml-auto engineering-input ${autoComputeTrg ? 'bg-muted' : ''}`}
                        data-testid={`input-layer-trg-${index}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLayer(layer.id)}
                        disabled={soilLayers.length <= 1}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        data-testid={`delete-layer-btn-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Layer depth validation */}
          {soilLayers.some((layer, i) => {
            if (i === 0) return false;
            return layer.from !== soilLayers[i - 1].to;
          }) && (
            <div className="mt-4 p-3 rounded bg-amber-500/10 border border-amber-500/20 text-sm text-amber-700 dark:text-amber-400">
              ⚠️ Layer depths are not continuous. Please check "From" and "To" values.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold font-mono text-[#00284F] dark:text-blue-400">
                {maxDepth.toFixed(1)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Max Depth (m)</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-mono">
                {soilLayers.length}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Layers</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-mono">
                {(soilLayers.reduce((sum, l) => sum + l.qu * (l.to - l.from), 0) / maxDepth || 0).toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Avg q<sub>u</sub> (MPa)</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-mono">
                {(soilLayers.reduce((sum, l) => sum + l.trg * (l.to - l.from), 0) / maxDepth || 0).toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Avg T<sub>RG</sub> (MPa)</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
